<template>
	<div class="clearfix">
		<!-- 移除隐藏的 input，由父级表单控制 -->
		<a-upload :customRequest="handleUpload" :file-list="fileList" :multiple="false" :beforeUpload="beforeUpload"
			@change="handleChange" @preview="handlePreview" :remove="handleRemove" :accept="acceptTypes"
			list-type="text">
			<a-button :disabled="fileList.length > 0">
				<a-icon type="upload" /> 上传视频
			</a-button>
			<div class="upload-hint">
				支持 {{ acceptTypesText }} 格式，最大 {{ maxSize }}MB，只能上传一个视频
			</div>
		</a-upload>

		<a-modal :visible="previewVisible" :footer="null" @cancel="previewVisible = false" width="70%">
			<video v-if="previewVisible" controls style="width: 100%; outline: none;height:600px" :src="currentPreviewVideo">
				您的浏览器不支持视频播放
			</video>
		</a-modal>
	</div>
</template>

<script>
	import {
		uploadVideo
	} from '@/api/app';

	export default {
		name: 'VideoUpload',
		props: {
			// 移除 value 和 rules 的 props，由 v-decorator 控制
			maxSize: {
				type: Number,
				default: 100 // MB
			},
			acceptTypes: {
				type: String,
				default: 'video/mp4,video/webm'
			},
			acceptTypesText: {
				type: String,
				default: 'MP4/WebM'
			},
			/** 视频上传成功时一并带回当前已选的封面地址（与 materials JSON 对齐） */
			coverUrl: {
				type: String,
				default: '',
			},
		},
		data() {
			return {
				fileList: [],
				previewVisible: false,
				currentPreviewVideo: ''
			};
		},
		methods: {
			/**
			 * 通过本地 video 元素读取时长（秒，四舍五入；失败或无法解析为 0）
			 */
			getVideoDuration(fileOrBlob) {
				const blob = fileOrBlob instanceof Blob ? fileOrBlob : (fileOrBlob && (fileOrBlob.originFileObj || fileOrBlob.file || fileOrBlob));
				if (!blob || !(blob instanceof Blob)) {
					return Promise.resolve(0);
				}
				return new Promise((resolve) => {
					const objectUrl = URL.createObjectURL(blob);
					const video = document.createElement('video');
					let settled = false;
					const finish = (seconds) => {
						if (settled) {
							return;
						}
						settled = true;
						URL.revokeObjectURL(objectUrl);
						video.removeAttribute('src');
						video.load();
						resolve(seconds);
					};
					const timer = setTimeout(() => finish(0), 15000);
					video.preload = 'metadata';
					video.muted = true;
					video.playsInline = true;
					video.src = objectUrl;
					video.onloadedmetadata = () => {
						clearTimeout(timer);
						let d = video.duration;
						if (!Number.isFinite(d) || d < 0) {
							d = 0;
						}
						finish(Math.round(d));
					};
					video.onerror = () => {
						clearTimeout(timer);
						finish(0);
					};
				});
			},
			emitVideoChange(url, duration) {
				const cover = typeof this.coverUrl === 'string' ? this.coverUrl.trim() : '';
				this.$emit('change', {
					url: url || '',
					duration: typeof duration === 'number' && Number.isFinite(duration) ? duration : 0,
					coverUrl: cover,
				});
			},
			beforeUpload(file) {
				const isValidType = this.acceptTypes.split(',').includes(file.type);
				if (!isValidType) {
					this.$message.error(`只能上传${this.acceptTypesText}格式的视频文件!`);
					return false;
				}

				const isLtSize = file.size / 1024 / 1024 < this.maxSize;
				if (!isLtSize) {
					this.$message.error(`视频大小不能超过${this.maxSize}MB!`);
					return false;
				}

				return true;
			},

			handleUpload({
				file,
				onProgress,
				onSuccess,
				onError
			}) {
				const rawFile = file.originFileObj || file;

				if (!file.uid) {
					file.uid = `video-${Date.now()}`;
				}

				this.getVideoDuration(rawFile).then((durationSec) => {
					const formData = new FormData();
					formData.append('video', rawFile);

					return uploadVideo(formData, {
						onUploadProgress: (progressEvent) => {
							const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
							onProgress({
								percent
							}, file);
						}
					}).then((response) => {
						if (response.isSucceed) {
							const videoUrl = response.data.url;
							this.emitVideoChange(videoUrl, durationSec);

							onSuccess({
								...response,
								data: {
									...response.data,
									uid: file.uid,
									duration: durationSec,
									coverUrl: typeof this.coverUrl === 'string' ? this.coverUrl.trim() : '',
								}
							}, file);
						} else {
							onError(new Error(response.message || '上传失败'), file);
						}
					});
				}).catch((error) => {
					onError(error, file);
				});
			},

			handleChange({
				file,
				fileList
			}) {
				this.fileList = fileList;
				if (file.status === 'done') {
					this.$message.success(`${file.name} 上传成功`);
				} else if (file.status === 'error') {
					this.$message.error(`${file.name} 上传失败`);
				}
			},

			handlePreview(file) {
				if (file.url || (file.response && file.response.data.url)) {
					this.currentPreviewVideo = file.url || file.response.data.url;
					this.previewVisible = true;
				}
			},

			handleRemove() {
				return new Promise((resolve) => {
					this.$confirm({
						title: '确认删除',
						content: '确定要删除这个视频吗？',
						onOk: () => {
							this.fileList = [];
							this.emitVideoChange('', 0);
							resolve(true);
						},
						onCancel: () => resolve(false)
					});
				});
			},

			// 添加清空方法
			clearFiles() {
				this.fileList = [];
				this.emitVideoChange('', 0);
			}
		}
	};
</script>

<style scoped>
	.upload-hint {
		color: rgba(0, 0, 0, 0.45);
		font-size: 12px;
		margin-top: 8px;
	}
</style>